import Spinner from "components/Spinner";
import StaticAutoComplete from "components/StaticAutoComplete";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import timezones from "timezones-list";

export default function AttSelectDevice({ showModal }) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  const [loading, setLoading] = useState(false);

  const [devicesList, setDevicesList] = useState(null);
  const [formData, setFormData] = useState(null);

  const [isUpdateMode, setIsUpdateMode] = useState(false);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  /*   Device Select Functions
   ********************************************* */
  const getDevicesList = async () => {
    try {
      loadingOn();
      const { data } = await API.get("/get-attendance-devices", {
        headers: authHeader(),
      });

      setDevicesList(data);
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const handleDeviceSelect = (device) => {
    // Set deviceId=device.id in url params using useSearchParams
    setSearchParams({ deviceId: device.id });
    showModal(false);
  };

  const addDevice = async (e) => {
    e.preventDefault();
    if (!formData) {
      dispatch(
        setNotification({
          message: "Please Fill All Fields",
          type: "error",
        })
      );
      return;
    }

    try {
      loadingOn();
      const { data } = await API.post("/add-attendance-devices", formData, {
        headers: authHeader(),
      });
      if (data?.success) {
        setDevicesList(data?.data);
        setFormData(data?.data);
        loadingOff();
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  // Update Device
  const updateDevice = async () => {
    try {
      loadingOn();
      const { data } = await API.patch(
        "/patch-attendance-devices/" + formData.id,
        {
          ...formData,
        },
        { headers: authHeader() }
      );
      if (data?.success) {
        setDevicesList(data.data);
        setFormData({});
        setIsUpdateMode(false);
        dispatch(
          setNotification({
            message: "Updated Successfully",
            type: "success",
          })
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  const handleUpdateClick = (device) => {
    setFormData(device);
    setIsUpdateMode(true);
  };

  // Delete Device
  const deleteDevice = async (id) => {
    try {
      loadingOn();
      const { data } = await API.delete("/delete-attendance-device/" + id, {
        headers: authHeader(),
      });

      if (data?.success) {
        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          })
        );
        await getDevicesList();
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  const handleTestConnection = async (device) => {
    try {
      loadingOn();
      const { data } = await API.get(
        "/test-device-connection/" + device.id,
        formData,
        {
          headers: authHeader(),
        }
      );

      if (data?.success) {
        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }

      // setDevicesList(data);
      // setFormData(data);
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    let clearTimeoutId = setTimeout(() => {
      getDevicesList();
    }, 100);
    return () => clearTimeout(clearTimeoutId);
  }, []);

  return (
    <>
      <h3>Devices List</h3>
      <hr className="mt-0" />
      {loading && <Spinner />}

      <div className="my-3">
        <h4>Add Device</h4>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!isUpdateMode) {
              addDevice(e);
            } else {
              updateDevice();
            }
          }}
          className="pt-2 px-2"
        >
          <div className="d-flex gap-2 ">
            <div className="align-middle ">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                value={formData?.name || ""}
                onChange={(e) => {
                  setFormData((prev) => {
                    return { ...prev, name: e.target.value };
                  });
                }}
                required
                placeholder="Enter Name"
                className="form-control"
              />
            </div>
            <div className="align-middle ">
              <label htmlFor="name">Ip Address</label>
              <input
                type="text"
                value={formData?.ipAddress || ""}
                onChange={(e) => {
                  setFormData((prev) => {
                    return { ...prev, ipAddress: e.target.value };
                  });
                }}
                required
                placeholder="Enter Ip Address"
                className="form-control"
              />
            </div>
            <div className="align-middle ">
              <label htmlFor="name">Port </label>
              <input
                type="text"
                required
                placeholder="4370"
                value={formData?.port || ""}
                onChange={(e) => {
                  setFormData((prev) => {
                    return { ...prev, port: e.target.value };
                  });
                }}
                className="form-control"
              />
            </div>
            <div className="align-middle ">
              <label htmlFor="timeZones">Time Zone </label>
              <StaticAutoComplete
                dataList={timezones}
                fieldName={"tzCode"}
                type={"text"}
                value={formData?.timezone || ""}
                handleChange={(fieldName, value) => {
                  setFormData((prev) => {
                    return { ...prev, timezone: value };
                  });
                }}
                resultsKey={"label"}
                searchKeys={["tzCode", "label"]}
                classNames={"form-control "}
                suggestionsWidth={300}
                placeholder="Select Time Zone"
              />
            </div>

            <div className="align-middle">
              <label htmlFor="timeZones"> </label>
              <div className="d-flex ">
                {!isUpdateMode ? (
                  <button type="submit" className="btn btn-primary ms-1">
                    Add
                  </button>
                ) : (
                  <>
                    <button type="submit" className="btn btn-success me-1">
                      Update
                    </button>
                    <button
                      onClick={() => {
                        setFormData({});
                        setIsUpdateMode(false);
                      }}
                      className="btn btn-warning"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
      <hr className="mt-4" />
      <h4 className="mt-3">Devices List</h4>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">ID</th>
            <th scope="col">Name</th>
            <th scope="col">Ip</th>
            <th className="" scope="col">
              <div className="text-center">Port</div>
            </th>

            <th scope="col">Timezone</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {devicesList?.map((device) => (
            <tr key={device.id}>
              <th className="align-items-middle" scope="row">
                {device.id}
              </th>
              <td className="align-items-middle">{device.name}</td>
              <td className="align-items-middle">{device.ipAddress}</td>
              <td className="align-items-middle">
                <div className="d-flex justify-content-center align-items-center">
                  <div>{device.port}</div>
                  <button
                    onClick={(e) => {
                      handleTestConnection(device);
                    }}
                    className="btn ms-2 px-1 btn-sm btn-success"
                  >
                    Test
                  </button>
                </div>
              </td>
              <td className="align-items-middle">{device.timezone}</td>
              <td className="align-items-middle">
                <button
                  onClick={(e) => {
                    handleDeviceSelect(device);
                  }}
                  className="btn btn-sm btn-success me-1"
                >
                  Select
                </button>

                <button
                  onClick={(e) => {
                    handleUpdateClick(device);
                  }}
                  className="btn btn-sm btn-info me-1"
                >
                  <i className="mdi mdi-pencil"></i>
                </button>

                {/* <DeleteModalButton
                  deleteFunc={deleteDevice}
                  deleteParams={device.id}
                /> */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
